import { Link } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { NotificheBell } from "../notifiche/NotificheBell";
import { LogoApp } from "../common/LogoApp";
import { tr } from "../../i18n";

interface AppHeaderProps {
  /** Titolo della sezione corrente (es. "ROSA", "MERCATO"). Se omesso: solo logo. */
  title?: string;
  /**
   * @deprecated Andrea 2026-05-23: il logo deve sempre dire "FANTASQUAD".
   * Il prop e' mantenuto per backward-compat (passato da molte pagine via
   * MobileShell) ma viene ignorato. Il nome della pagina si vede nel `title`
   * grande in oro sotto al logo, non al posto del brand.
   */
  logoSuffix?: string;
  /** Slot azioni a destra (icone custom) */
  rightSlot?: React.ReactNode;
  /** Andrea 2026-05-23: handler per aprire il drawer mobile (hamburger
   * a sinistra del logo). Se non passato, l'hamburger non viene mostrato. */
  onOpenDrawer?: () => void;
}

export function AppHeader({
  title,
  // logoSuffix: ignorato di proposito (vedi JSDoc sopra).
  rightSlot,
  onOpenDrawer,
}: AppHeaderProps) {
  const { user, logout } = useAuth();

  return (
    <header className="bg-primary-deep relative overflow-hidden">
      {/* Background pattern: linee verticali sottili (campo da gioco vibe) */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none opacity-40"
        style={{
          backgroundImage:
            "repeating-linear-gradient(90deg, transparent 0, transparent 40px, rgba(232,223,250,0.03) 40px, rgba(232,223,250,0.03) 41px)",
        }}
      />
      <div className="relative max-w-md mx-auto px-5 py-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          {/* Andrea 2026-05-23: hamburger button per aprire il drawer
              con tutte le pagine accessorie (admin, tools, ecc.). */}
          {onOpenDrawer && (
            <button
              onClick={onOpenDrawer}
              className="w-9 h-9 rounded-md hover:bg-primary/40 text-fg flex items-center justify-center transition shrink-0"
              aria-label={tr("Apri menu")}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="w-5 h-5"
                aria-hidden
              >
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </svg>
            </button>
          )}
          <Link to="/" className="flex items-center gap-2 min-w-0">
            <LogoApp className="w-8 h-8 drop-shadow-lg shrink-0" />
            <h1 className="font-display text-lg tracking-[0.08em] truncate">
              <span className="text-accent">FANTA</span>
              <span className="text-fg">SQUAD</span>
            </h1>
          </Link>
        </div>

        <div className="flex items-center gap-2">
          {rightSlot}
          <NotificheBell />
          <button
            onClick={logout}
            className="w-8 h-8 rounded-full bg-primary/40 hover:bg-primary text-fg flex items-center justify-center transition"
            title={tr("{{nome}} — clicca per uscire", { nome: user?.nome_completo })}
            aria-label={tr("Logout")}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="w-4 h-4"
              aria-hidden
            >
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
          </button>
        </div>
      </div>

      {title && (
        <div className="relative max-w-md mx-auto px-5 pb-3">
          <h2 className="font-display text-2xl tracking-wider text-accent">{title}</h2>
        </div>
      )}
    </header>
  );
}
